import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import api from "../services/api";
import { FiCreditCard, FiSmartphone, FiCircle } from "react-icons/fi";

// Get Stripe key from runtime config - only initialize if key exists
const getStripeKey = () =>
  window.ENV?.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  "";

// Lazy load Stripe only when needed and key is available
let stripePromise = null;
const getStripe = () => {
  const key = getStripeKey();
  if (!key) return null;
  if (!stripePromise) {
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

function CheckoutForm({ clientSecret, priceDetails, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setLoading(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout/success`,
      },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message);
      setLoading(false);
    } else if (paymentIntent?.status === "succeeded") {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <div className="alert alert-error mt-4">{error}</div>}
      <button
        type="submit"
        className="btn btn-primary"
        style={{ width: "100%", marginTop: "24px" }}
        disabled={!stripe || loading}
      >
        {loading
          ? "Processing..."
          : `Pay ${priceDetails?.formattedPrice || ""}`}
      </button>
    </form>
  );
}

function Checkout() {
  const { priceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [priceDetails, setPriceDetails] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pixData, setPixData] = useState(null);
  const [btcData, setBtcData] = useState(null);

  useEffect(() => {
    if (priceId) {
      fetchPriceDetails();
    }
  }, [priceId]);

  const fetchPriceDetails = async () => {
    try {
      const response = await api.get(`/prices/${priceId}`);
      setPriceDetails(response.data);

      // Create initial checkout session for card
      if (response.data) {
        await initializeCheckout("stripe");
      }
    } catch (err) {
      setError("Failed to load price details");
    } finally {
      setLoading(false);
    }
  };

  const initializeCheckout = async (provider) => {
    try {
      if (provider === "stripe") {
        const response = await api.post("/payments/create-checkout", {
          priceId,
          provider: "stripe",
        });
        setClientSecret(response.data.clientSecret);
      } else if (provider === "mercadopago") {
        const response = await api.post("/payments/pix", {
          priceId,
        });
        setPixData(response.data);
      } else if (provider === "btcpay") {
        const response = await api.post("/payments/bitcoin", {
          priceId,
        });
        setBtcData(response.data);
      }
    } catch (err) {
      setError("Failed to initialize checkout");
    }
  };

  const handlePaymentMethodChange = async (method) => {
    setPaymentMethod(method);
    if (method === "pix" && !pixData) {
      await initializeCheckout("mercadopago");
    } else if (method === "bitcoin" && !btcData) {
      await initializeCheckout("btcpay");
    }
  };

  const handleSuccess = () => {
    navigate("/checkout/success");
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="checkout-container">
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-container">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-card">
        <div className="checkout-product">
          <h2 className="checkout-product-name">
            {priceDetails?.product?.name || "Product"}
          </h2>
          <div className="checkout-product-price">
            {formatCurrency(priceDetails?.amount, priceDetails?.currency)}
            {priceDetails?.billing_period && (
              <span>/{priceDetails.billing_period}</span>
            )}
          </div>
          {priceDetails?.product?.description && (
            <p className="text-muted" style={{ marginTop: "8px" }}>
              {priceDetails.product.description}
            </p>
          )}
        </div>

        <h3 style={{ marginBottom: "16px" }}>Payment Method</h3>

        <div className="payment-options">
          <div
            className={`payment-option ${
              paymentMethod === "card" ? "selected" : ""
            }`}
            onClick={() => handlePaymentMethodChange("card")}
          >
            <div className="payment-option-icon">
              <FiCreditCard size={24} />
            </div>
            <div>
              <div className="payment-option-name">Credit / Debit Card</div>
              <div className="payment-option-desc">
                Pay securely with your card
              </div>
            </div>
          </div>

          <div
            className={`payment-option ${
              paymentMethod === "pix" ? "selected" : ""
            }`}
            onClick={() => handlePaymentMethodChange("pix")}
          >
            <div className="payment-option-icon">
              <FiSmartphone size={24} />
            </div>
            <div>
              <div className="payment-option-name">PIX</div>
              <div className="payment-option-desc">
                Instant payment for Brazil
              </div>
            </div>
          </div>

          <div
            className={`payment-option ${
              paymentMethod === "bitcoin" ? "selected" : ""
            }`}
            onClick={() => handlePaymentMethodChange("bitcoin")}
          >
            <div className="payment-option-icon">
              <FiCircle size={24} />
            </div>
            <div>
              <div className="payment-option-name">Bitcoin</div>
              <div className="payment-option-desc">
                Pay with BTC or Lightning
              </div>
            </div>
          </div>
        </div>

        {/* Card Payment - only show if Stripe is configured */}
        {paymentMethod === "card" && !getStripe() && (
          <div className="alert alert-warning" style={{ marginTop: "16px" }}>
            Card payments are not configured. Please contact the administrator
            or choose another payment method.
          </div>
        )}
        {paymentMethod === "card" && clientSecret && getStripe() && (
          <Elements
            stripe={getStripe()}
            options={{
              clientSecret,
              appearance: {
                theme: "night",
                variables: {
                  colorPrimary: "#6366f1",
                  colorBackground: "#1e293b",
                  colorText: "#f8fafc",
                  borderRadius: "8px",
                },
              },
            }}
          >
            <CheckoutForm
              clientSecret={clientSecret}
              priceDetails={{
                ...priceDetails,
                formattedPrice: formatCurrency(
                  priceDetails?.amount,
                  priceDetails?.currency
                ),
              }}
              onSuccess={handleSuccess}
            />
          </Elements>
        )}

        {/* PIX Payment */}
        {paymentMethod === "pix" && pixData && (
          <div style={{ textAlign: "center" }}>
            <p className="text-muted mb-4">
              Scan the QR code with your banking app
            </p>
            {pixData.qrCodeBase64 && (
              <img
                src={`data:image/png;base64,${pixData.qrCodeBase64}`}
                alt="PIX QR Code"
                style={{ maxWidth: "250px", margin: "0 auto 16px" }}
              />
            )}
            <div className="form-group">
              <label className="form-label">Or copy the PIX code:</label>
              <input
                type="text"
                className="form-input"
                value={pixData.qrCode || ""}
                readOnly
                onClick={(e) => {
                  e.target.select();
                  navigator.clipboard.writeText(pixData.qrCode);
                }}
              />
            </div>
            <p className="text-muted" style={{ fontSize: "12px" }}>
              Payment expires in 30 minutes
            </p>
          </div>
        )}

        {/* Bitcoin Payment */}
        {paymentMethod === "bitcoin" && btcData && (
          <div style={{ textAlign: "center" }}>
            <p className="text-muted mb-4">
              Send Bitcoin to complete your payment
            </p>
            <div
              style={{
                background: "var(--bg-tertiary)",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#f7931a",
                }}
              >
                {btcData.btcAmount} BTC
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Bitcoin Address:</label>
              <input
                type="text"
                className="form-input"
                value={btcData.btcAddress || ""}
                readOnly
                onClick={(e) => {
                  e.target.select();
                  navigator.clipboard.writeText(btcData.btcAddress);
                }}
              />
            </div>
            {btcData.checkoutLink && (
              <a
                href={btcData.checkoutLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ width: "100%" }}
              >
                Open BTCPay Checkout
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Checkout;
